import syriusLogo from "./../../../assets/logos/syrius-logo-padded.svg";
import Client, { SignClient } from "@walletconnect/sign-client";
import { WalletConnectModal, WalletConnectModalConfig } from "@walletconnect/modal";
import { SessionTypes, PairingTypes } from "@walletconnect/types";
import { Primitives } from "znn-ts-sdk";
import { toast } from "react-toastify";
import logoIcon from "./../../../assets/logos/zenon-big.png";
import { ConfigCtrl } from "@walletconnect/modal-core";
import { deleteRecentWalletsFromLocalStorage } from "../../../utils/utils";
import {
  addToNamespacePairings,
  allNamespaces,
  defaultModalConfigCtrlState,
  getLatestActivePairing,
  getLatestActivePairingWithNoNamespaceAssigned,
  getLatestActiveSession,
} from "../../../utils/wcUtils";
import constants from "../../../utils/constants";
import { InternalWalletInfo, internalNetworkProviderTypes } from "./internalNetworkContext";

const walletConnectProjectId = "6106aa8c2f308b338f31465bef999a1f";
const desktopWallets = [
  {
    id: "syrius",
    name: "Syrius",
    links: {
      native: "syrius:",
      universal: constants.officialSyriusWalletUrl,
    },
  },
];
const walletImages = {
  syrius: syriusLogo,
};
const themeVariables = {
  "--wcm-font-family": `-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif`,
  "--wcm-background-color": "#26BA3F",
  "--wcm-accent-color": "#67E646",
  "--wcm-button-border-radius	": "8px",
  "--wcm-wallet-icon-border-radius": "8px",
  "--wcm-secondary-button-border-radius": "8px",
};

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

const initClient = async () => {
  const signClient = await SignClient.init({
    projectId: walletConnectProjectId,
    metadata: {
      // name: document.title,
      // description: (document.querySelector('meta[name="description"]') as any)?.content,
      name: "Zenon Bridge",
      description: "Bridge tokens, add and stake liquidity",
      url: window.location.origin,
      icons: [window.location.origin + logoIcon],
    },
  });
  return signClient;
};

const WC_REQUEST_TIMEOUT_MS = 30000;

const ensureRelayConnected = async (signClient: Client) => {
  if (!signClient.core.relayer.connected) {
    console.warn("[WC-DEBUG] Relay disconnected, attempting to reconnect...");
    try {
      await signClient.core.relayer.transportOpen();
      await delay(2000);
      console.log("[WC-DEBUG] Relay reconnected:", signClient.core.relayer.connected);
    } catch (err) {
      console.error("[WC-DEBUG] Failed to reconnect relay:", err);
    }
  }
};

const withTimeout = <T,>(promise: Promise<T>, ms: number, context: string): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`[WC-DEBUG] ${context} timed out after ${ms}ms`)), ms)
    ),
  ]);
};

const initModal = async () => {
  try {
    ConfigCtrl.state = { ...defaultModalConfigCtrlState };
    const wcConfig: WalletConnectModalConfig = {
      // ...ConfigCtrl.state,
      projectId: walletConnectProjectId,
      chains: allNamespaces.zenon.chains,
      themeVariables: themeVariables,
      mobileWallets: [],
      desktopWallets: desktopWallets,
      walletImages: walletImages,
      explorerRecommendedWalletIds: [],
      explorerExcludedWalletIds: [
        "c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96",
        "1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369",
        "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0",
      ],
      enableExplorer: false,
      themeMode: "light",
    };

    ConfigCtrl.state = wcConfig;

    deleteRecentWalletsFromLocalStorage();
    const wcModal: WalletConnectModal = new WalletConnectModal(wcConfig);
    console.log("3. ConfigCtrl.state", { ...ConfigCtrl.state });
    ConfigCtrl.setConfig(wcConfig);
    await delay(1000);
    console.log("4. ConfigCtrl.state", { ...ConfigCtrl.state });

    return wcModal;
  } catch (err) {
    //
    // Bug in the WalletConnect package
    // https://github.com/WalletConnect/walletconnect-monorepo/issues/4257
    //
    console.error("Error initializing WalletConnectModal", err);
    throw new Error("Error initializing WalletConnectModal");
  }
};

const connect = async (signClient: Client, onDismiss?: (reason: string) => unknown) => {
  console.log("Connecting wallet");
  const wcModal: WalletConnectModal = await initModal();

  wcModal.subscribeModal((state: any) => console.log("wcModal.subscribeModal", state));

  const latestPairing = getLatestActivePairing(signClient, "zenon");
  //
  // First step is to find out if we are already paired with a Wallet
  //
  if (latestPairing) {
    //
    // If we are paired we search for an available session.
    // Sessions can expire and we need to make sure they are still available
    //
    const latestSession = getLatestActiveSession(signClient, "zenon");
    if (latestSession) {
      //
      // If we are paired we search for an available session.
      //
      console.log("[CONNECTED] Found pairing and session. Already connected on ", latestPairing, latestSession);
      return { session: latestSession, pairing: latestPairing };
    } else {
      //
      //  Old Pairing, New Session
      //
      console.log("[CONNECTED] Creating new session on pairing", latestPairing);

      //
      // Version 1
      //
      const retrievedSession = await signClient.connect({
        pairingTopic: latestPairing.topic,
        requiredNamespaces: { zenon: allNamespaces.zenon },
      });
      console.log("retrievedSession", retrievedSession);

      //
      // Version 1.2
      //
      // await retrievedSession.approval();
      // signClient.approve()
      console.log("retrievedSession after approval", retrievedSession);

      // We usually got some errors if not adding the delay. Sessions were not updated as soon as the await finished
      await delay(5000);
      const newSession = getLatestActiveSession(signClient, "zenon");
      console.log("[CONNECTED] New session", newSession);
      return { session: newSession, pairing: latestPairing };
    }
  } else {
    //
    // New Pairing, New Pairing
    //
    console.log("[CONNECTED] Creating new pairing and session");
    const { uri, approval } = await signClient.connect({
      requiredNamespaces: { zenon: allNamespaces.zenon },
    });
    console.log("Generated uri", uri);
    if (uri) {
      try {
        await wcModal.openModal({
          requiredNamespaces: { zenon: allNamespaces.zenon },
          walletConnectChainIds: [],
          // optionalNamespaces: allNamespaces,
          uri: uri,
        });

        // await wcModal.openModal({standaloneChains: ["zenon:1"], uri: uri});
        wcModal.subscribeModal((state: any) => {
          if (state.open == false) {
            console.log("state", state);
            if (onDismiss) {
              onDismiss("User closed modal");
            }
          }
        });
        const session = await approval();
        console.log("[CONNECTED] Session", session);
        console.log("[WC-DEBUG] Session details after approval:", {
          topic: session.topic,
          namespaces: session.namespaces,
          peer: session.peer,
          acknowledged: session.acknowledged,
          expiry: session.expiry,
          relayConnected: signClient.core.relayer.connected,
          userAgent: navigator.userAgent,
        });
        wcModal.closeModal();
        //
        // We usually got some errors if not adding the delay.
        // The new pairings was not in the list as soon as the await finished
        //
        await delay(5000);
        console.log("[WC-DEBUG] After 5s delay - relay connected:", signClient.core.relayer.connected);

        // Important !!!
        //
        // For now, we assume that the latest active pairing is from the current namespace
        // And that the user didn't trigger another pairing creation meanwhile.
        //
        // const newPairing = getLatestActivePairing(signClient);
        const newPairing = getLatestActivePairingWithNoNamespaceAssigned(signClient);

        // We save this latest pairing as being from this namespace and treat it as that
        //
        addToNamespacePairings("zenon", newPairing);

        console.log("[CONNECTED] newPairing", newPairing);
        return { session: session, pairing: newPairing };
      } catch (err) {
        console.error("Error approving", err);
        wcModal.closeModal();
        throw err;
      } finally {
        wcModal.closeModal();
      }
    } else {
      wcModal.closeModal();
      throw Error("Couldn't generate URI on new session or topic");
    }
  }
};

const getInfo = async (signClient: Client, session: SessionTypes.Struct) => {
  console.log("getInfo", signClient, session);
  console.log("[WC-DEBUG] getInfo - session details:", {
    topic: session.topic,
    expiry: session.expiry,
    acknowledged: session.acknowledged,
    relayConnected: signClient.core.relayer.connected,
    userAgent: navigator.userAgent,
  });

  await ensureRelayConnected(signClient);

  console.log("[WC-DEBUG] Sending znn_info request on topic:", session.topic);
  const result: InternalWalletInfo = await withTimeout(
    signClient.request({
      topic: session.topic,
      chainId: allNamespaces.zenon.chains[0],
      request: {
        method: "znn_info",
        params: undefined,
      },
    }),
    WC_REQUEST_TIMEOUT_MS,
    `znn_info request (topic: ${session.topic}, relay: ${signClient.core.relayer.connected})`
  );
  console.log("znn_info res", result);
  return result;
};

const signTransaction = async (signClient: Client, session: SessionTypes.Struct, params: any) => {
  console.log("signTransaction - params", params);
  console.log("signTransaction", signClient, session);

  await ensureRelayConnected(signClient);

  const signature = await withTimeout(
    signClient.request({
      topic: session.topic,
      chainId: allNamespaces.zenon.chains[0],
      request: {
        method: "znn_sign",
        params: JSON.stringify(params.accountBlock),
      },
    }),
    WC_REQUEST_TIMEOUT_MS,
    `znn_sign request (topic: ${session.topic})`
  );
  console.log("znn_sign result", signature);

  return signature;
};

const sendTransaction = async (signClient: Client, session: SessionTypes.Struct, params: any) => {
  console.log("sendTransaction - params", params);
  console.log("sendTransaction", signClient, session);

  await ensureRelayConnected(signClient);

  const result = await withTimeout(
    signClient.request({
      topic: session.topic,
      chainId: allNamespaces.zenon.chains[0],
      request: {
        method: "znn_send",
        params: {
          fromAddress: params.fromAddress,
          accountBlock: params.accountBlock,
        },
      },
    }),
    WC_REQUEST_TIMEOUT_MS,
    `znn_send request (topic: ${session.topic})`
  );
  console.log("znn_send result", result);

  const accountBlock = Primitives.AccountBlockTemplate.fromJson(result || "{}");

  console.log("sendTransaction - accountBlock", accountBlock);
  return accountBlock;
};

const disconnectPairing = async (
  signClient: Client,
  pairing: PairingTypes.Struct,
  reasonMessage?: string,
  reasonData?: string
) => {
  try {
    console.log("Disconnecting, ", signClient, pairing);
    await signClient.core.pairing.disconnect({ topic: pairing.topic });
    // await signClient.disconnect({
    //   topic: pairing.topic,
    //   reason: {
    //     code: 1,
    //     message: reasonMessage || "Default Message",
    //     data: reasonData || "Default Data",
    //   },
    // });
    console.log("localStorage", localStorage);
    return true;
  } catch (err: any) {
    console.error(err);
    const readableError = err?.message || JSON.stringify(err);
    // toast(`Error: ${readableError}`, {
    //   position: "bottom-center",
    //   autoClose: 5000,
    //   hideProgressBar: false,
    //   closeOnClick: true,
    //   pauseOnHover: false,
    //   draggable: true,
    //   type: "error",
    //   theme: "dark",
    // });
  }
};

const disconnectSession = async (
  signClient: Client,
  session: SessionTypes.Struct | { topic: string },
  reasonMessage?: string,
  reasonData?: string
) => {
  try {
    console.log("Disconnecting, ", signClient, session);

    await signClient.disconnect({
      topic: session.topic,
      reason: {
        code: 1,
        message: reasonMessage || "Default Message",
        data: reasonData || "Default Data",
      },
    });

    // await signClient.session.delete(session.topic, {
    //   code: 1,
    //   message: reasonMessage || "Default Message",
    //   data: reasonData || "Default Data",
    // });

    return true;
  } catch (err: any) {
    console.error(err);
    const readableError = err?.message || JSON.stringify(err);
    // toast(`Error: ${readableError}`, {
    //   position: "bottom-center",
    //   autoClose: 5000,
    //   hideProgressBar: false,
    //   closeOnClick: true,
    //   pauseOnHover: false,
    //   draggable: true,
    //   type: "error",
    //   theme: "dark",
    // });
  }
};

const disconnectAllPairings = async (signClient: Client, reasonMessage?: string, reasonData?: string) => {
  console.log("Disconnecting all pairings, ", signClient, reasonMessage);
  try {
    return Promise.all(
      signClient.pairing.getAll().map(async (pairing) => {
        return await disconnectPairing(signClient, pairing, reasonMessage, reasonData);
      })
    );
  } catch (err: any) {
    console.error(err);
    const readableError = err?.message || JSON.stringify(err);
    // toast(`Error: ${readableError}`, {
    //   position: "bottom-center",
    //   autoClose: 5000,
    //   hideProgressBar: false,
    //   closeOnClick: true,
    //   pauseOnHover: false,
    //   draggable: true,
    //   type: "error",
    //   theme: "dark",
    // });
  }
};

const disconnectAllSessions = async (signClient: Client, reasonMessage?: string, reasonData?: string) => {
  console.log("Disconnecting all sessions, ", signClient, reasonMessage);
  try {
    return Promise.all(
      signClient.session.getAll().map(async (session) => {
        return await disconnectSession(signClient, session, reasonMessage, reasonData);
      })
    );
  } catch (err: any) {
    console.error(err);
    const readableError = err?.message || JSON.stringify(err);
    // toast(`Error: ${readableError}`, {
    //   position: "bottom-center",
    //   autoClose: 5000,
    //   hideProgressBar: false,
    //   closeOnClick: true,
    //   pauseOnHover: false,
    //   draggable: true,
    //   type: "error",
    //   theme: "dark",
    // });
  }
};

const registerEvents = (
  signClient: Client,
  onDisconnect: () => Promise<boolean>,
  onAddressChange: (newAddress: string, providerType: internalNetworkProviderTypes) => unknown,
  onChainIdChange: (newChainId: string, providerType: internalNetworkProviderTypes) => unknown,
  onNodeChange: (newChainId: string, providerType: internalNetworkProviderTypes) => unknown
) => {
  console.log("internalNetwork - registerEvents");
  // Subscribe to events

  // Available events
  // type Event = "session_proposal" | "session_update" | "session_extend" | "session_ping" | "session_delete" | "session_expire" | "session_request" | "session_request_sent" | "session_event" | "proposal_expire";

  signClient.core.on("disconnect", (args: any) => {
    console.log(".on internal disconnect", args);
    // disconnectAllSessions(signClient);
    // disconnectSession(signClient, { topic: args?.topic }, "Disconnected from the Wallet App");
    onDisconnect();
  });

  signClient.core.on("wc_pairingDelete", (args: any) => {
    console.log(".on internal wc_pairingDelete", args);
  });

  signClient.core.on("wc_sessionUpdate", (args: any) => {
    console.log(".on internal wc_sessionUpdate", args);
  });

  signClient.core.on("wc_sessionUpdate", (args: any) => {
    console.log(".on internal wc_sessionUpdate", args);
  });

  signClient.core.on("addressChange", (args: any) => {
    console.log(".on internal addressChange", args);
  });

  signClient.core.on("chainIdChange", (args: any) => {
    console.log(".on internal chainIdChange", args);
  });

  signClient.core.relayer.on("onRelayDisconnected", (args: any) => {
    console.log(".on internal onRelayDisconnected", args);
  });

  signClient.on("session_proposal", (args: any) => {
    console.log(".on internal session_proposal (should only be listened by the wallet)", args);
  });

  signClient.on("session_update", (args: any) => {
    console.log(".on internal session_update (should only be listened by the wallet)", args);
  });

  signClient.on("session_extend", (args: any) => {
    console.log(".on internal session_extend", args);
  });

  signClient.on("session_ping", (args: any) => {
    console.log(".on internal session_ping", args);
  });

  signClient.on("session_delete", (args: any) => {
    console.log(".on internal session_delete", args);
    // disconnectSession(signClient, { topic: args?.topic }, "Session deleted from the Wallet App");
    onDisconnect();
  });

  signClient.on("session_expire", (args: any) => {
    console.log(".on internal session_expire", args);
    // disconnectSession(signClient, { topic: args?.topic }, "Session expired");
    onDisconnect();
  });

  signClient.on("session_request", (args: any) => {
    console.log("[WC-DEBUG] .on internal session_request", args);
  });

  signClient.on("session_request_sent", (args: any) => {
    console.log("[WC-DEBUG] .on internal session_request_sent", args);
  });

  signClient.on("session_event", (args: any) => {
    // This is where chainIdChange and addressChange happens
    console.log(".on internal network session_event", args);
    const [networkId, chainId] = args?.params?.chainId?.split(":");
    console.log("networkId", networkId);
    console.log("chainId", chainId);

    // ToDo: do something about the hardcoded 'zenon' chainId'
    if (networkId === "zenon") {
      console.log("args?.params?.event?.name", args?.params?.event?.name);
      switch (args?.params?.event?.name) {
        case "addressChange": {
          const newAddress = args?.params?.event?.data?.toLowerCase();
          if (!newAddress?.length) {
            onDisconnect();
          } else {
            console.log("addressChanged to", newAddress);
            onAddressChange(newAddress, internalNetworkProviderTypes.walletConnect);
          }
          break;
        }
        case "chainIdChange": {
          const newChainId = args?.params?.event?.data;
          console.log("chainIdChanged to", newChainId);
          onChainIdChange(newChainId, internalNetworkProviderTypes.walletConnect);
          break;
        }
        case "nodeChange": {
          const newChainId = args?.params?.event?.data;
          console.log("nodeChanged to", newChainId);
          onNodeChange(newChainId, internalNetworkProviderTypes.walletConnect);
          break;
        }

        default: {
          console.log("Unhandled event triggered", args?.params?.event?.name);
          break;
        }
      }
    }
  });

  signClient.on("session_event", (args: any) => {
    // Because of a weird walletConnect bug, we need to register this event twice
    // Otherwise, registering the externalNetwork session event won't work
    //
    // Might be because of the internal implementation of signClient.on()
    // and the way it registers multiple callbacks for the same event
    console.log(".on internal network session_event 2", args);
  });

  signClient.on("proposal_expire", (args: any) => {
    // ToDo: Implement this
    console.log(".on proposal_expire", args);
  });
};

const internalNetworkWalletConnectWrapper = {
  initClient,
  initModal,
  connect,
  getInfo,
  signTransaction,
  sendTransaction,
  disconnectAllPairings,
  disconnectAllSessions,
  disconnectPairing,
  disconnectSession,
  registerEvents,
};

export default internalNetworkWalletConnectWrapper;
